import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { ClassificationView, DivergenceCause } from "@/types/closed-loop.types";
import { CauseOption } from "./CauseOption";
import { EvidencePanel } from "./EvidencePanel";
import { TargetPicker } from "./TargetPicker";
import { useConfirmClassification } from "./hooks";

interface ClassificationFormProps {
  appliedScenarioId: string;
  lineId: string;
  classification: ClassificationView;
  /** Chamado depois de gravar — quem abriu o formulário para RECLASSIFICAR o fecha de novo. */
  onDone?: () => void;
}

const ALL_CAUSES: readonly DivergenceCause[] = [
  "WITHIN_TOLERANCE",
  "EXECUTION_NON_ADHERENCE",
  "UNMODELED_EVENT",
  "INCORRECT_ASSUMPTION",
  "STRUCTURAL_MODEL_ERROR",
];

// Spec 077 (US1) — o formulário de classificação: a proposta com a evidência, as cinco causas (as
// vedadas visíveis e explicadas), o alvo quando for premissa incorreta, e a justificativa.
//
// ⚠️ A justificativa é obrigatória nos TRÊS casos de V3 e o botão reflete isso ANTES do envio: a
// regra não soube propor, quem julga discordou da proposta, ou já existe um diagnóstico e ele está
// sendo trocado. O servidor recusa com 422 de qualquer forma — o que a tela faz é não deixar a
// pessoa descobrir a exigência depois de ter escrito nada.

export function ClassificationForm({
  appliedScenarioId,
  lineId,
  classification,
  onDone,
}: ClassificationFormProps) {
  const proposal = classification.proposal;
  const [cause, setCause] = useState<DivergenceCause | null>(proposal?.cause ?? null);
  const [note, setNote] = useState("");
  const [target, setTarget] = useState<{
    machineId: string | null;
    parameterId: string | null;
  }>({ machineId: null, parameterId: null });
  const confirm = useConfirmClassification();

  const blockedOf = (c: DivergenceCause) =>
    classification.blockedCauses.find((b) => b.cause === c)?.reason ?? null;

  const isReclassification = classification.current !== null;
  const noteRequired =
    proposal?.cause == null || (cause !== null && cause !== proposal.cause) || isReclassification;
  const needsTarget = cause === "INCORRECT_ASSUMPTION";
  const targetComplete = !!target.machineId && !!target.parameterId;

  const canSubmit =
    cause !== null &&
    blockedOf(cause) === null &&
    (!noteRequired || note.trim().length > 0) &&
    (!needsTarget || targetComplete) &&
    !confirm.isPending;

  function submit() {
    if (cause === null) return;
    confirm.mutate(
      {
        appliedScenarioId,
        lineId,
        cause,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(needsTarget && target.machineId && target.parameterId
          ? { target: { machineId: target.machineId, parameterId: target.parameterId } }
          : {}),
      },
      {
        onSuccess: () => {
          setNote("");
          onDone?.();
        },
      }
    );
  }

  return (
    <div className="space-y-3">
      {proposal ? (
        <EvidencePanel evidence={proposal.evidence} needsDetail={proposal.needsDetail} />
      ) : null}

      <fieldset className="space-y-2">
        <legend className="text-[13px] font-medium text-text-primary">
          Por que este compromisso divergiu?
        </legend>
        {ALL_CAUSES.map((c) => (
          <CauseOption
            key={c}
            cause={c}
            selected={cause === c}
            blockedReason={blockedOf(c)}
            isProposal={proposal?.cause === c}
            onSelect={setCause}
          />
        ))}
      </fieldset>

      {needsTarget ? (
        <TargetPicker
          lineId={lineId}
          committedMachineIds={classification.committedMachineIds}
          machineId={target.machineId}
          parameterId={target.parameterId}
          onChange={setTarget}
        />
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor="classification-note">
          Justificativa{noteRequired ? "" : " (opcional)"}
        </Label>
        <Input
          id="classification-note"
          value={note}
          maxLength={1000}
          placeholder="Ex.: a linha parou por falta de matéria-prima na segunda metade do turno"
          onChange={(e) => setNote(e.target.value)}
        />
        {/* ⚠️ A reclassificação vem PRIMEIRO. Quando já existe um diagnóstico, o servidor deixa de
            enviar a proposta (R1) — e a ordem antiga fazia a tela dizer "o sistema não teve como
            propor uma causa" numa ocasião em que ele propôs e alguém aceitou. */}
        <p className="text-[12px] text-text-secondary">
          {isReclassification
            ? "Trocar o diagnóstico registrado exige dizer o que mudou: o anterior continua no histórico, marcado como superado."
            : proposal?.cause == null
              ? "O sistema não teve como propor uma causa — o que você escreve aqui é o único registro do que sustenta esta escolha."
              : cause !== null && cause !== proposal.cause
                ? "Você escolheu uma causa diferente da proposta — a justificativa é o que permitirá, depois, avaliar quem estava certo."
                : "Concordar com a proposta não exige justificativa. Escreva se houver algo que o número não conta."}
        </p>
      </div>

      <div className="flex justify-end">
        <Button disabled={!canSubmit} onClick={submit}>
          {confirm.isPending ? "Registrando…" : "Registrar causa"}
        </Button>
      </div>
    </div>
  );
}
